import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Dictation button powered by OpenAI Whisper (via Emergent Universal LLM Key).
 * - No hard time limit (up to ~15 min / 25 MB)
 * - Shows a live elapsed timer while recording
 * - Uploads a MediaRecorder blob to POST /audio/transcribe
 * - Appends transcript text to the target field via onAppend
 * Works on all major browsers including iOS Safari.
 */
export const MicButton = ({ onAppend, className = "", label = "Dictate" }) => {
    const [state, setState] = useState("idle"); // idle | recording | uploading
    const [elapsed, setElapsed] = useState(0);
    const [supported, setSupported] = useState(true);

    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const startTsRef = useRef(0);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            setSupported(false);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
            }
        };
    }, []);

    const startTimer = () => {
        startTsRef.current = Date.now();
        setElapsed(0);
        timerRef.current = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startTsRef.current) / 1000));
        }, 250);
    };

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const pickMime = () => {
        const candidates = [
            "audio/webm;codecs=opus",
            "audio/webm",
            "audio/mp4",
            "audio/aac",
            "audio/ogg;codecs=opus",
        ];
        for (const m of candidates) {
            if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
                return m;
            }
        }
        return "";
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mime = pickMime();
            const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            rec.onstop = async () => {
                stopTimer();
                const type = rec.mimeType || mime || "audio/webm";
                const blob = new Blob(chunksRef.current, { type });
                chunksRef.current = [];
                // release mic
                stream.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
                await uploadAndTranscribe(blob, type);
            };
            recorderRef.current = rec;
            rec.start(1000); // gather chunks every 1s so long recordings are safe
            startTimer();
            setState("recording");
            toast.success("Recording — talk as long as you need, click Stop when done");
        } catch (err) {
            const msg = String(err?.message || err || "");
            if (/NotAllowedError|Permission/i.test(msg)) {
                toast.error("Microphone permission denied");
            } else {
                toast.error("Could not start recording");
            }
        }
    };

    const stopRecording = () => {
        const rec = recorderRef.current;
        if (!rec) return;
        setState("uploading");
        try {
            rec.stop();
        } catch (err) {
            console.debug("recorder stop error", err);
        }
    };

    const uploadAndTranscribe = async (blob, mimeType) => {
        try {
            const ext =
                mimeType.includes("mp4") || mimeType.includes("m4a") ? "m4a" :
                mimeType.includes("wav") ? "wav" :
                mimeType.includes("ogg") ? "ogg" : "webm";
            const fd = new FormData();
            fd.append("audio", blob, `recording.${ext}`);
            const { data } = await api.post("/audio/transcribe", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 180000,
            });
            const text = (data?.text || "").trim();
            if (text) {
                onAppend(text);
                toast.success("Transcript added");
            } else {
                toast.error("Nothing was transcribed — please try again");
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || "";
            toast.error(detail ? `Transcription failed: ${detail}` : "Transcription failed");
        } finally {
            setState("idle");
            setElapsed(0);
        }
    };

    const toggle = () => {
        if (state === "idle") startRecording();
        else if (state === "recording") stopRecording();
        // uploading state has no action
    };

    if (!supported) return null;

    const mmss = `${String(Math.floor(elapsed / 60)).padStart(1, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
    const isRec = state === "recording";
    const isUp = state === "uploading";

    return (
        <button
            type="button"
            onClick={toggle}
            disabled={isUp}
            aria-label={isRec ? "Stop dictation" : "Start dictation"}
            title={label}
            className={`inline-flex items-center gap-2 border-2 border-un-onyx px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                isRec
                    ? "bg-un-berry text-white animate-pulse"
                    : isUp
                    ? "bg-un-onyx text-white cursor-wait"
                    : "bg-white text-un-onyx hover:bg-un-onyx hover:text-white"
            } ${className}`}
            data-testid="mic-button"
        >
            {isUp ? (
                <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Transcribing…
                </>
            ) : isRec ? (
                <>
                    <Square className="w-4 h-4 fill-current" />
                    <span className="tabular-nums">Stop · {mmss}</span>
                </>
            ) : (
                <>
                    <Mic className="w-4 h-4" />
                    {label}
                </>
            )}
        </button>
    );
};
